import React, { useState, useEffect } from 'react';
import { Dialog, DialogActions, DialogContent, DialogTitle, TextField, Button, Select, MenuItem, FormControl, InputLabel, Snackbar, Grid } from '@mui/material';
import { getDatabase, ref, get, update, remove } from 'firebase/database';
import { useNavigate } from 'react-router-dom';
import { Autocomplete } from '@mui/material'; // Dodaj import
const EventDialog = ({ open, onClose, event, onSave, onDelete }) => {
  const navigate = useNavigate();
  const convertToLocalDateTime = (date) => {
    const localDate = new Date(date);
    return new Date(localDate.getTime() - localDate.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);
  };

  const db = getDatabase();

  const [title, setTitle] = useState(event ? event.title : '');
  const [startDate, setStartDate] = useState(event ? convertToLocalDateTime(event.startDate) : '');
  const [endDate, setEndDate] = useState(event ? convertToLocalDateTime(event.endDate) : '');
  const [selectedClient, setSelectedClient] = useState(event ? event.clientId : '');
  const [selectedEmployee, setSelectedEmployee] = useState(event ? event.employeeId : '');
  const [price, setPrice] = useState(event ? event.price : '');
  const [additionalProducts, setAdditionalProducts] = useState([]);
  const [error, setError] = useState('');
  const [errorFields, setErrorFields] = useState({
    title: false,
    startDate: false,
    endDate: false,
    selectedClient: false,
    selectedEmployee: false,
    price: false,
  });

  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [products, setProducts] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [successSnackbarOpen, setSuccessSnackbarOpen] = useState(false);

  useEffect(() => {
    const fetchEmployeesAndClientsAndProducts = async () => {
      const employeesRef = ref(db, 'employees');
      const clientsRef = ref(db, 'clients');
      const productsRef = ref(db, 'products');

      const [employeesSnapshot, clientsSnapshot, productsSnapshot] = await Promise.all([
        get(employeesRef), get(clientsRef), get(productsRef)
      ]);

      if (employeesSnapshot.exists()) {
        const employeesData = [];
        employeesSnapshot.forEach((childSnapshot) => {
          employeesData.push({ id: childSnapshot.key, ...childSnapshot.val() });
        });
        setEmployees(employeesData);
      }

      if (clientsSnapshot.exists()) {
        const clientsData = [];
        clientsSnapshot.forEach((childSnapshot) => {
          clientsData.push({ id: childSnapshot.key, ...childSnapshot.val() });
        });
        clientsData.sort((a, b) => a.name.localeCompare(b.name));
        setClients(clientsData);
      }

      if (productsSnapshot.exists()) {
        const productsData = [];
        productsSnapshot.forEach((childSnapshot) => {
          productsData.push({ id: childSnapshot.key, ...childSnapshot.val() });
        });
        setProducts(productsData);
      }
    };

    fetchEmployeesAndClientsAndProducts();
  }, [db]);

  useEffect(() => {
    if (event) {
      const eventRef = ref(db, `events/${event.id}`);
      const fetchEventDetails = async () => {
        const eventSnapshot = await get(eventRef);
        if (eventSnapshot.exists()) {
          const eventData = eventSnapshot.val();
          setTitle(eventData.title || '');
          setStartDate(convertToLocalDateTime(eventData.startDate) || '');
          setEndDate(convertToLocalDateTime(eventData.endDate) || '');
          setSelectedClient(eventData.clientId || '');
          setSelectedEmployee(eventData.employeeId || '');
          setPrice(eventData.price || '');
          setAdditionalProducts(
            (eventData.additionalProducts || []).map((prod) => ({
              ...prod,
              isNew: false,
            }))
          );
        }
      };
      fetchEventDetails();
    }
  }, [event, db]);

  const handleSave = async () => {
    let hasError = false;
    const newErrorFields = {
      title: !title,
      startDate: !startDate,
      endDate: !endDate,
      selectedClient: !selectedClient,
      selectedEmployee: !selectedEmployee,
      price: !price,
    };

    const startDay = new Date(startDate).toISOString().slice(0, 10);
    const endDay = new Date(endDate).toISOString().slice(0, 10);

    if (Object.values(newErrorFields).includes(true)) {
      setErrorFields(newErrorFields);
      setError('Żadne pole nie może być puste.');
      hasError = true;
    } else if (new Date(endDate) <= new Date(startDate)) {
      setError('Data zakończenia musi być późniejsza niż data rozpoczęcia.');
      hasError = true;
    } else if (startDay !== endDay) {
      setError('Dzień rozpoczęcia i zakończenia musi być taki sam.');
      hasError = true;
    } else {
      setError('');
    }

    if (hasError) return;

    try {
      const selectedClientData = clients.find(client => client.id === selectedClient);
      const selectedEmployeeData = employees.find(emp => emp.id === selectedEmployee);

      const updatedEvent = {
        title,
        startDate: new Date(startDate).toISOString(),
        endDate: new Date(endDate).toISOString(),
        color: selectedEmployeeData?.color || '',
        employeeName: selectedEmployeeData?.name || '',
        employeeNumber: selectedEmployeeData?.number || '',
        name: selectedClientData?.name || '',
        phone: selectedClientData?.number || '',
        clientId: selectedClient,
        employeeId: selectedEmployee,
        price,
        additionalProducts
      };

      const eventRef = ref(db, `events/${event.id}`);
      await update(eventRef, updatedEvent);

      await Promise.all(additionalProducts.map(async ({ productId, quantity, isNew }) => {
        if (isNew && productId && quantity) {
          const productRef = ref(db, `products/${productId}`);
          const productSnapshot = await get(productRef);
          if (productSnapshot.exists()) {
            const productData = productSnapshot.val();
            const newQuantity = Math.max(productData.quantity - parseFloat(quantity), 0);
            await update(productRef, { quantity: newQuantity });
          }
        }
      }));

      const undefinedEventRef = ref(db, 'events/undefined');
      await remove(undefinedEventRef);
      localStorage.setItem('SAVE', 'true');
      window.location.reload();
      setError('');
      setErrorFields({
        title: false,
        startDate: false,
        endDate: false,
        selectedClient: false,
        selectedEmployee: false,
        price: false,
      });

      onSave(updatedEvent);
      onClose();
    } catch (error) {
      console.error('Error saving event:', error);
      setError('Wystąpił błąd podczas zapisywania.');
    }
  };

  const handleDelete = () => {
    setConfirmDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const eventRef = ref(db, `events/${event.id}`);
      await remove(eventRef);
      onDelete();
      setSuccessSnackbarOpen(true);
    } catch (error) {
      console.error('Error deleting event:', error);
      setError('Wystąpił błąd podczas usuwania wydarzenia.');
    }
    setConfirmDeleteOpen(false);
  };

  const handleCloseConfirmDelete = () => {
    setConfirmDeleteOpen(false);
  };

  const handleAddProduct = () => {
    setAdditionalProducts([...additionalProducts, { productId: '', quantity: '', isNew: true }]);
  };

  const handleProductChange = (index, event) => {
    const updatedProducts = [...additionalProducts];
    updatedProducts[index].productId = event.target.value;
    setAdditionalProducts(updatedProducts);
  };

  const handleQuantityChange = (index, event) => {
    const updatedProducts = [...additionalProducts];
    updatedProducts[index].quantity = event.target.value;
    setAdditionalProducts(updatedProducts);
  };

  // Removing the product is only allowed for new products
  const handleRemoveProduct = (index) => {
    const updatedProducts = additionalProducts.filter((_, i) => i !== index);
    setAdditionalProducts(updatedProducts);
  };

  const handleAddNewEmployee = () => {
    navigate('/AddEmployee');
  };
  const handleAddNewClient = () => {
    navigate('/AddClient');
  };
  const handleAddNewProduct = () => {
    navigate('/AddProduct');
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{event ? 'Edytuj Wydarzenie' : 'Dodaj Wydarzenie'}</DialogTitle>
      <DialogContent>
        {/* Tytuł Usługi (100% szerokości) */}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              autoFocus
              margin="dense"
              label="Tytuł"
              fullWidth
              variant="outlined"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              error={errorFields.title}
              helperText={errorFields.title ? 'Tytuł jest wymagany' : ''}
            />
          </Grid>

          {/* Data i godzina rozpoczęcia (50%) i Data i godzina zakończenia (50%) */}
          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="Data rozpoczęcia"
              type="datetime-local"
              fullWidth
              variant="outlined"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              error={errorFields.startDate}
              helperText={errorFields.startDate ? 'Data rozpoczęcia jest wymagana' : ''}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              margin="dense"
              label="Data zakończenia"
              type="datetime-local"
              fullWidth
              variant="outlined"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              error={errorFields.endDate}
              helperText={errorFields.endDate ? 'Data zakończenia jest wymagana' : ''}
            />
          </Grid>

          {/* Pracownik (50%) i Klient (50%) */}
          <Grid item xs={6}>
            <FormControl fullWidth margin="dense" error={errorFields.selectedEmployee}>
              <InputLabel>Pracownik</InputLabel>
              <Select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                renderValue={(selected) => {
                  const employee = employees.find(emp => emp.id === selected);
                  if (!employee) return '';  // Zabezpieczenie przed undefined
                  return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          backgroundColor: employee.color || 'transparent', // Ustawienie domyślnego koloru
                          borderRadius: '4px',
                          border: '1px solid black',
                          marginRight: '10px'
                        }}
                      />
                      {employee.name}
                    </div>
                  );
                }}
              >
                {employees.map((employee) => (
                  <MenuItem key={employee.id} value={employee.id}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <div
                        style={{
                          width: 20,
                          height: 20,
                          backgroundColor: employee.color || 'transparent', // Ustawienie domyślnego koloru
                          borderRadius: '4px',
                          border: '1px solid black',
                          marginRight: '10px'
                        }}
                      />
                      {employee.name}
                    </div>
                  </MenuItem>
                ))}
                <MenuItem value="" onClick={handleAddNewEmployee}>
                  + DODAJ NOWEGO PRACOWNIKA
                </MenuItem>
              </Select>
              {errorFields.selectedEmployee && <div>Pracownik jest wymagany</div>}
            </FormControl>

          </Grid>

          <Grid item xs={6}>
  <Autocomplete
    options={[...clients, { id: 'add-new', name: '+ DODAJ NOWEGO KLIENTA' }]} // Dodanie opcji "DODAJ NOWEGO KLIENTA"
    getOptionLabel={(option) => option.name || ''} // Wyświetlanie nazwy klienta
    filterOptions={(options, { inputValue }) =>
      options.filter((client) =>
        client.name.toLowerCase().includes(inputValue.toLowerCase()) ||
        client.id === 'add-new' // Umożliwia wyświetlenie "DODAJ NOWEGO KLIENTA" mimo braku dopasowania
      )
    }
    value={clients.find(client => client.id === selectedClient) || null} // Znalezienie wybranego klienta na podstawie ID
    onChange={(event, newValue) => {
      if (newValue && newValue.id === 'add-new') {
        handleAddNewClient(); // Przekierowanie do dodawania nowego klienta
      } else {
        setSelectedClient(newValue ? newValue.id : ''); // Ustawienie ID wybranego klienta
      }
    }}
    renderInput={(params) => (
      <TextField
        {...params}
        label="Klient"
        margin="dense"
        error={errorFields.selectedClient}
        helperText={errorFields.selectedClient ? 'Klient jest wymagany' : ''}
      />
    )}
  />
</Grid>


          {/* Ilość produktów (100% szerokości) */}
          <Grid item xs={12}>
            <h3>Produkty:</h3>
          </Grid>

          {/* Produkty: Produkt + Ilość + Typ produktu + Usuń (100%) */}
          {additionalProducts.map((product, index) => {
            const productType = products.find((prod) => prod.id === product.productId)?.type || '';

            return (
              <Grid container key={index} spacing={2}>
                <Grid item xs={4}>
                  <FormControl fullWidth style={{ marginTop: "20px" }}>
                    <InputLabel>Produkt</InputLabel>
                    <Select
                      value={product.productId}
                      onChange={(e) => handleProductChange(index, e)}
                      disabled={!product.isNew} // Disable if not new
                    >
                      {products.map((prod) => (
                        <MenuItem key={prod.id} value={prod.id}>
                          {prod.name}
                        </MenuItem>
                      ))}
                      
                    <MenuItem value="" onClick={handleAddNewProduct}>
                      + DODAJ NOWY PRODUKT
                    </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={4} style={{ marginTop: "20px" }}>
                  <TextField
                    label="Ilość"
                    type="number"
                    fullWidth
                    value={product.quantity}
                    onChange={(e) => handleQuantityChange(index, e)}
                    disabled={!product.isNew} // Disable if not new
                  />
                </Grid>

                <Grid item xs={4} style={{ marginTop: "30px" }}>
                  <span>{productType}</span>
                  {product.isNew && (
                    <Button
                      style={{ marginLeft: '20px', marginTop: "0px" }}
                      onClick={() => handleRemoveProduct(index)}
                      color="secondary"
                      variant="outlined"
                    >
                      Usuń
                    </Button>
                  )}
                </Grid>
              </Grid>
            );
          })}

          {/* Dodaj produkt button */}
          <Grid item xs={12}>
            <Button onClick={handleAddProduct} color="primary" variant="outlined">
              Dodaj Produkt
            </Button>
          </Grid>

          {/* Cena (100% szerokości) */}
          <Grid item xs={12}>
            <TextField
              margin="dense"
              label="Cena"
              type="number"
              fullWidth
              variant="outlined"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              error={errorFields.price}
              helperText={errorFields.price ? 'Cena jest wymagana' : ''}
            />
          </Grid>

          {/* Error message */}
          {error && (
            <Grid item xs={12}>
              <div style={{ color: 'red' }}>{error}</div>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Anuluj
        </Button>
        <Button onClick={handleSave} color="primary">
          Zapisz
        </Button>
        <Button onClick={handleDelete} color="secondary">
          Usuń
        </Button>
      </DialogActions>

      {/* Delete Confirmation Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={handleCloseConfirmDelete}>
        <DialogTitle>Czy chcesz usunąć tę usługę?</DialogTitle>
        <DialogActions>
          <Button onClick={handleCloseConfirmDelete} color="primary">
            NIE
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary">
            TAK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for successful deletion */}
      <Snackbar
        open={successSnackbarOpen}
        onClose={() => setSuccessSnackbarOpen(false)}
        message="Wydarzenie zostało pomyślnie usunięte."
      />
    </Dialog>
  );

};

export default EventDialog;
